import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import './cloudinaryFileWidget.css';

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

const parseFileJson = (value) => {
  try {
    const parsed = JSON.parse(value);
    return parsed && parsed.url ? parsed : null;
  }
  catch (e) {
    return null;
  }
};

class CloudinaryFileWidget extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      uploading: false,
      error: null,
    };
  }

  get isMultiple() {
    const { schema } = this.props;
    return schema.type === 'array';
  }

  get files() {
    const { value } = this.props;
    if (!value) return [];

    if (this.isMultiple) {
      if (!Array.isArray(value)) return [];
      return value.map(parseFileJson).filter(Boolean);
    }

    const parsed = parseFileJson(value);
    return parsed ? [parsed] : [];
  }

  handleUploadClick = () => {
    const { formContext, disabled } = this.props;
    if (disabled) return;

    const cloudName = formContext && formContext.cloudinaryCloudName;
    const uploadPreset = formContext && formContext.cloudinaryUploadPreset;

    if (!cloudName || !uploadPreset || !window.cloudinary) {
      this.setState({ error: 'File upload is available when the form is published' });
      return;
    }

    this.setState({ error: null, loading: true });

    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName,
        uploadPreset,
        maxFileSize: MAX_FILE_SIZE,
        multiple: this.isMultiple,
        resourceType: 'auto',
        clientAllowedFormats: null,
        showPoweredBy: false,
        sources: ['local'],
        singleUploadAutoClose: !this.isMultiple,
      },
      (err, result) => {
        if (err) {
          this.setState({ uploading: false, error: err.message || 'Upload failed' });
          return;
        }

        if (result.event === 'show') {
          this.setState({ loading: false });
        }

        if (result.event === 'queues-start') {
          this.setState({ uploading: true });
        }

        if (result.event === 'success') {
          const { info } = result;
          const uploaded = {
            url: info.secure_url,
            fileName: info.original_filename + '.' + info.format, // eslint-disable-line prefer-template
            publicId: info.public_id,
            resourceType: info.resource_type,
          };
          this.addFile(uploaded);
        }

        if (result.event === 'close') {
          this.setState({ loading: false, uploading: false });
        }
      },
    );

    widget.open();
  };

  addFile = (uploaded) => {
    const { onChange, value } = this.props;
    const json = JSON.stringify(uploaded);

    if (this.isMultiple) {
      const current = Array.isArray(value) ? value : [];
      onChange([...current, json]);
    }
    else {
      onChange(json);
    }
  };

  handleRemove = (index) => {
    const { onChange, value } = this.props;

    if (this.isMultiple) {
      const current = Array.isArray(value) ? value : [];
      const updated = current.filter((_, i) => i !== index);
      onChange(updated.length > 0 ? updated : undefined);
    }
    else {
      onChange(undefined);
    }
  };

  render() {
    const { disabled, id } = this.props;
    const { loading, uploading, error } = this.state;
    const { files } = this;

    return (
      <div className="cfw" id={id}>
        {files.length > 0 && (
          <ul className="cfw__file-list">
            {files.map((file, index) => (
              <li key={file.publicId || index} className="cfw__file-item">
                <svg className="cfw__file-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M9.333 1.333H4a1.333 1.333 0 00-1.333 1.334v10.666A1.333 1.333 0 004 14.667h8a1.333 1.333 0 001.333-1.334V5.333l-4-4z" stroke="#5986ff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9.333 1.333v4h4" stroke="#5986ff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="cfw__file-name">{file.fileName}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="cfw__remove-btn"
                    onClick={() => this.handleRemove(index)}
                    aria-label="Remove file"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M10.5 3.5l-7 7M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className="cfw__upload-btn"
          onClick={this.handleUploadClick}
          disabled={disabled || loading || uploading}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {loading ? 'Opening...' : (uploading ? 'Uploading...' : 'Choose file')}
        </button>

        {error && (
          <p className="cfw__error">{error}</p>
        )}
      </div>
    );
  }
}

CloudinaryFileWidget.defaultProps = {
  value: undefined,
  disabled: false,
  formContext: {},
};

CloudinaryFileWidget.propTypes = {
  id: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    type: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  formContext: PropTypes.shape({
    cloudinaryCloudName: PropTypes.string,
    cloudinaryUploadPreset: PropTypes.string,
  }),
};

export default CloudinaryFileWidget;
